import clsx from 'clsx';
import { Button, Flex, Icon, IconButton, Text, iconWeight, useReducedMotion } from '@nula/core-ui';
import { formatElapsed } from '@/lib/recordings/elapsed';
import type { LiveRecordingPhase } from '@/lib/recordings/use-live-recording';
import styles from './record-transport.module.css';

/** Six bars, staggered in CSS. Keys are stable so React never reorders them. */
const WAVE_BARS = [0, 1, 2, 3, 4, 5];

interface RecordTransportProps {
  entityId: string;
  /** Lifecycle phase, derived from the synced state (useLiveRecording) by the
   *  caller — NOT a local sim — so this transport stays in sync with the HUD.
   *  null/undefined = no live recording (idle). 'capturing' = live controls;
   *  'processing' = post-capture upload/transcribe → "Saving…", no controls. */
  phase?: LiveRecordingPhase | null;
  /** Paused state, synced cross-window via the recording://paused broadcast.
   *  Only meaningful in the 'capturing' phase; ignored when idle. */
  isPaused?: boolean;
  /** Authoritative elapsed ms (paused spans excluded), from the registry
   *  broadcast via the caller. The single value every surface shows. */
  elapsedMs?: number | null;
  /** A finished recording exists for this entity: render the disabled
   *  "Recorded" pill (one recording per entity). Only consulted when idle. */
  hasRecording?: boolean;
  /** Fired on Record click (idle) — the caller starts a recording into this
   *  entity. Only invoked when `showStart` is set. */
  onStart?: () => void;
  /** Show the idle Record affordance. Off by default — callers opt in when the
   *  entity has no recording yet. */
  showStart?: boolean;
  onPause?: () => void;
  onResume?: () => void;
  onStop?: () => void;
  className?: string;
}

/**
 * Recording transport — a CONTROLLED, fully-derived reflection of the
 * synced recording state (the caller passes phase/isPaused/elapsedMs from
 * useLiveRecording + the authoritative elapsed broadcast). It holds NO local
 * state, so it stays centralized and in sync with the HUD: pause/start in either
 * surface is reflected here, the elapsed is the same value everywhere, and the
 * controls drive the same real commands.
 *
 * "Saving…" is the real 'processing' phase (driven by recording status), NOT a
 * timer — so it persists exactly until processing completes, with no flicker.
 */
export function RecordTransport({
  entityId: _entityId,
  phase,
  isPaused,
  elapsedMs,
  hasRecording = false,
  onStart,
  showStart = false,
  onPause,
  onResume,
  onStop,
  className,
}: RecordTransportProps) {
  const shouldReduce = useReducedMotion();

  // Processing (post-capture: uploading/transcribing/…). Real-status-driven, so
  // it holds until the recording actually finishes — no timer, no flicker.
  if (phase === 'processing') {
    return (
      <Flex align="center" className={clsx(styles.chip, styles.stopping, className)}>
        <span className={styles.spinner} aria-hidden />
        <Text size="xs" className={styles.savingLabel}>
          Saving…
        </Text>
      </Flex>
    );
  }

  // Capturing → live transport (pause/stop + authoritative elapsed).
  if (phase === 'capturing') {
    const elapsed = elapsedMs != null ? formatElapsed(elapsedMs) : null;
    return (
      <Flex
        align="center"
        gap="xs"
        className={clsx(styles.chip, styles.capsule, isPaused ? styles.paused : styles.live, className)}
      >
        <Flex
          component="span"
          data-wave
          className={clsx(styles.wave, !isPaused && !shouldReduce && styles.animating)}
          aria-hidden
        >
          {WAVE_BARS.map((bar) => (
            <span key={bar} data-wave-bar className={styles.waveBar} />
          ))}
        </Flex>
        {elapsed !== null && (
          <Text size="xs" className={styles.timer}>
            {elapsed}
          </Text>
        )}
        {isPaused ? (
          <IconButton
            size="sm"
            transparent
            className={clsx(styles.control, styles.controlPrimary)}
            aria-label="Resume recording"
            onClick={onResume}
          >
            <Icon name="play_arrow" size={14} weight={iconWeight.regular} />
          </IconButton>
        ) : (
          <IconButton
            size="sm"
            transparent
            className={clsx(styles.control, styles.controlPrimary)}
            aria-label="Pause recording"
            onClick={onPause}
          >
            <Icon name="pause" size={14} weight={iconWeight.regular} />
          </IconButton>
        )}
        <span className={styles.controlDivider} aria-hidden />
        <IconButton
          size="sm"
          transparent
          className={clsx(styles.control, styles.controlSecondary)}
          aria-label="Stop recording"
          onClick={onStop}
        >
          <Icon name="stop" size={14} weight={iconWeight.regular} />
        </IconButton>
      </Flex>
    );
  }

  // Not in progress. A finished recording → muted "Recorded" pill.
  if (hasRecording) {
    return (
      <Button
        variant="text"
        size="sm"
        disabled
        aria-label="Recorded"
        className={clsx(styles.chip, styles.recordedPill, className)}
      >
        <Icon name="check_circle" size={14} weight={iconWeight.regular} />
        <Text size="xs" className={styles.recordedLabel}>
          Recorded
        </Text>
      </Button>
    );
  }

  // Idle. The Record affordance is gated behind showStart until start-into-an-
  // existing-entity is wired on the backend (fast-follow). Default: render
  // nothing so the entity header stays clean.
  if (!showStart) return null;
  return (
    <Button
      variant="text"
      size="sm"
      aria-label="Start recording"
      onClick={onStart}
      className={clsx(styles.chip, styles.recordPill, className)}
    >
      <span className={styles.dot} aria-hidden />
      <Text size="xs" className={styles.recordLabel}>
        Record
      </Text>
    </Button>
  );
}
